import { type ChangeEvent, type ClipboardEvent, FC, type KeyboardEvent, type RefCallback } from 'react';
import { cn } from '@/shared/lib';
import { padTimePart, readTimePartInput, TIME_PART_LENGTH, type TimePart } from './timeParts';

type Props = {
  part: TimePart;
  value: string;
  label: string;
  placeholder: string;
  inputRef: RefCallback<HTMLInputElement>;
  onChange: (value: string) => void;
  onPaste: (event: ClipboardEvent<HTMLInputElement>) => void;
  /** Переход к соседней части: стрелкой у края части, стиранием пустой части, заполнением части */
  onFocusPrev: () => void;
  onFocusNext: () => void;
};

/**
 * Одна часть поля времени: только цифры своей длины; заполненная часть передаёт ввод следующей,
 * а цифра, набранная в уже заполненную часть, начинает её заново.
 */
export const TranscriptTimePartInput: FC<Props> = ({
  part,
  value,
  label,
  placeholder,
  inputRef,
  onChange,
  onPaste,
  onFocusPrev,
  onFocusNext,
}) => {
  const length = TIME_PART_LENGTH[part];

  const applyInput = (input: string) => {
    const digits = readTimePartInput(part, input);
    onChange(digits);
    if (digits.length === length) onFocusNext();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => applyInput(event.target.value);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const { selectionStart, selectionEnd } = event.currentTarget;
    // Заполненная часть не примет цифру без выделения — набор в неё начинается заново при любом
    // положении курсора, как в поле времени kit
    const isDigit = /^\d$/.test(event.key) && !event.ctrlKey && !event.metaKey && !event.altKey;
    if (isDigit && value.length === length) {
      event.preventDefault();
      applyInput(event.key);
      return;
    }
    const isCaretAtStart = selectionStart === 0 && selectionEnd === 0;
    const isCaretAtEnd = selectionStart === value.length && selectionEnd === value.length;
    if ((event.key === 'Backspace' && value === '') || (event.key === 'ArrowLeft' && isCaretAtStart)) {
      event.preventDefault();
      onFocusPrev();
    }
    if (event.key === 'ArrowRight' && isCaretAtEnd) {
      event.preventDefault();
      onFocusNext();
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      aria-label={label}
      placeholder={placeholder}
      maxLength={length}
      value={value}
      className={cn(
        'min-w-0 bg-transparent p-0 text-center text-sm outline-none placeholder:text-muted-foreground',
        length === 3 ? 'w-8' : 'w-6',
      )}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={(event) => event.currentTarget.select()}
      // Дописанная до полной длины часть читается однозначно: `5` в миллисекундах — это `500`
      onBlur={() => value !== padTimePart(part, value) && onChange(padTimePart(part, value))}
      onPaste={onPaste}
    />
  );
};
